import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { gameLoadFailureMessage } from "../lib/walletMessages";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { PasskeySettings } from "../components/PlayerSignin";
import VerifyPhoneDialog from "../components/VerifyPhoneDialog";
import ReferralCard from "../components/ReferralCard";
import WalletHeroCard from "../components/WalletHeroCard";
import AppDownloadCard from "../components/AppDownloadCard";
import PlinkoHomeGame from "../components/PlinkoHomeGame";
import CrashHomeGame from "../components/CrashHomeGame";
import { VerifiedName } from "../components/VerifiedBadge";
import {
  CalendarDays,
  Phone,
  ChevronLeft,
  ChevronRight,
  Flame,
  Trophy,
  History,
  Megaphone,
  BadgeDollarSign,
  Gift,
  IdCard,
  Lock,
  RotateCw,
  CheckCircle2,
  Target,
  User,
  Users,
  ExternalLink,
  ShieldCheck,
  MapPin,
} from "lucide-react";
import { siteConfig } from "../lib/siteConfig";
import { useTheme } from "../context/ThemeContext";
import {
  formatKycPendingReason,
  formatKycRejectionReason,
  isKycExpiredReason,
  kycPrepItems,
  kycRetryLabel,
  US_STATES,
} from "../lib/kyc";

const FAKE_WINS = [
  "Jenny P wins $250 on Juwa 2.0",
  "Marcus L wins $480 on FireKirin",
  "Mellisa wins $120 on Vegas Sweeps",
  "Daniel K wins $1,200 on Cash Frenzy",
  "Aisha M wins $90 on Orion Stars",
  "Ben T wins $760 on GameVault",
];
const DAILY_SPIN_REVEAL_MS = 2600;

export default function Dashboard() {
  const { user, refreshUser, login } = useAuth();
  const { isDark } = useTheme();
  const userId = user && user !== false ? user.id : "";
  const savedFullName = user && user !== false ? (user.full_name || "") : "";
  const savedDateOfBirth = user && user !== false ? (user.date_of_birth || "") : "";
  const savedState = user && user !== false ? (user.state || "") : "";
  const identityStatus = user && user !== false ? (user.identity_verification_status || "not_submitted") : "not_submitted";
  const identityVerificationRequired = user && user !== false ? user.identity_verification_required !== false : true;
  const [history, setHistory] = useState([]);
  const [walletActivity, setWalletActivity] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [rewardClaim, setRewardClaim] = useState(null);
  const [rewardClaiming, setRewardClaiming] = useState(false);
  const [achievements, setAchievements] = useState(null);
  const [achievementClaiming, setAchievementClaiming] = useState("");
  const [birthdayBonus, setBirthdayBonus] = useState(null);
  const [birthdayClaiming, setBirthdayClaiming] = useState(false);
  const [dailySpin, setDailySpin] = useState(null);
  const [dailySpinClaiming, setDailySpinClaiming] = useState(false);
  const [dailySpinResult, setDailySpinResult] = useState(null);
  const [dailySpinResultVisible, setDailySpinResultVisible] = useState(false);
  const [playStreak, setPlayStreak] = useState(null);
  const [playStreakClaiming, setPlayStreakClaiming] = useState("");
  const [hotGames, setHotGames] = useState([]);
  const [communications, setCommunications] = useState({ notice_message: "", bad_beat_jackpot: 0, notifications: [] });
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [bonusClaiming, setBonusClaiming] = useState(false);
  const [emailCode, setEmailCode] = useState("");
  const [emailCodeOpen, setEmailCodeOpen] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailVerifying, setEmailVerifying] = useState(false);
  const [identityForm, setIdentityForm] = useState({ fullName: "", dateOfBirth: "", state: "" });
  const [identitySubmitting, setIdentitySubmitting] = useState(false);
  const [tickerIdx, setTickerIdx] = useState(0);
  const [hotAutoPaused, setHotAutoPaused] = useState(false);
  const carouselRef = useRef(null);
  const dailySpinRevealTimer = useRef(null);

  const loadLeaderboardRewardClaim = useCallback(() => (
    api.get("/me/leaderboard-reward")
      .then(({ data }) => setRewardClaim(data))
      .catch(() => setRewardClaim(null))
  ), []);
  const loadAchievements = useCallback(() => (
    api.get("/me/achievements")
      .then(({ data }) => setAchievements(data))
      .catch(() => setAchievements(null))
  ), []);
  const loadBirthdayBonus = useCallback(() => (
    api.get("/me/birthday-bonus")
      .then(({ data }) => setBirthdayBonus(data))
      .catch(() => setBirthdayBonus(null))
  ), []);
  const loadDailySpin = useCallback(() => (
    api.get("/me/daily-spin")
      .then(({ data }) => setDailySpin(data))
      .catch(() => setDailySpin(null))
  ), []);
  const loadPlayStreak = useCallback(() => (
    api.get("/me/play-streak")
      .then(({ data }) => setPlayStreak(data))
      .catch(() => setPlayStreak(null))
  ), []);
  const loadWalletActivity = useCallback(() => (
    api.get("/me/requests")
      .then(({ data }) => setWalletActivity(data || []))
      .catch(() => {})
  ), []);

  useEffect(() => {
    api.get("/me/history").then(({ data }) => setHistory(data)).catch(() => {});
    loadWalletActivity();
    api.get("/leaderboard").then(({ data }) => setLeaderboard(data)).catch(() => {});
    loadLeaderboardRewardClaim();
    loadAchievements();
    loadBirthdayBonus();
    loadDailySpin();
    loadPlayStreak();
    api.get("/games").then(({ data }) => setHotGames(data.filter((g) => g.status === "live"))).catch(() => {});
    api.get("/communications").then(({ data }) => {
      setCommunications({
        ...(data.settings || {}),
        notifications: data.notifications || [],
      });
    }).catch(() => {});
  }, [loadAchievements, loadBirthdayBonus, loadDailySpin, loadLeaderboardRewardClaim, loadPlayStreak, loadWalletActivity]);

  useEffect(() => () => {
    if (dailySpinRevealTimer.current) {
      window.clearTimeout(dailySpinRevealTimer.current);
    }
  }, []);

  useEffect(() => {
    if (!userId) return;
    setIdentityForm((current) => ({
      ...current,
      fullName: savedFullName || current.fullName || "",
      dateOfBirth: savedDateOfBirth || current.dateOfBirth || "",
      state: savedState || current.state || "",
    }));
  }, [userId, savedFullName, savedDateOfBirth, savedState]);

  useEffect(() => {
    if (!userId || typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("kyc") !== "didit") return;
    api.post("/me/identity-verification/didit-refresh")
      .then(async () => {
        await refreshUser?.();
        const status = params.get("status") || "";
        if (status === "approved") {
          toast.success("ID verification approved");
        } else if (status === "rejected") {
          toast.error("ID verification needs to be resubmitted");
        } else {
          toast.success("KYC submitted. We will notify you when the review is finished.");
        }
      })
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => {
        params.delete("kyc");
        params.delete("status");
        params.delete("verificationSessionId");
        params.delete("verification_session_id");
        params.delete("session_id");
        params.delete("sessionId");
        params.delete("refreshed");
        const nextSearch = params.toString();
        const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ""}${window.location.hash || ""}`;
        window.history.replaceState({}, "", nextUrl);
      });
  }, [userId, refreshUser]);

  useEffect(() => {
    if (!userId || !identityVerificationRequired || identityStatus !== "pending" || typeof window === "undefined") return undefined;
    let active = true;
    let checking = false;
    const refreshDiditStatus = async () => {
      if (checking || document.hidden) return;
      checking = true;
      try {
        const { data } = await api.post("/me/identity-verification/didit-refresh");
        if (active && data?.user) {
          await refreshUser?.();
        } else if (active && data?.status && data.status !== "pending") {
          await refreshUser?.();
        }
      } catch {
        // Webhook is still the primary path; retry quietly while the card is pending.
      } finally {
        checking = false;
      }
    };
    const timer = window.setInterval(refreshDiditStatus, 5000);
    refreshDiditStatus();
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [userId, identityStatus, identityVerificationRequired, refreshUser]);

  useEffect(() => {
    const t = setInterval(() => setTickerIdx((i) => (i + 1) % FAKE_WINS.length), 3500);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (hotAutoPaused || hotGames.length < 3) return undefined;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return undefined;

    const timer = setInterval(() => {
      const el = carouselRef.current;
      if (!el || document.hidden) return;
      const maxScroll = el.scrollWidth - el.clientWidth;
      if (maxScroll <= 8) return;

      const card = el.querySelector("[data-hot-game-card]");
      const cardWidth = card?.getBoundingClientRect().width || Math.min(260, el.clientWidth * 0.72);
      const step = cardWidth + 16;
      const next = el.scrollLeft + step;

      el.scrollTo({
        left: next >= maxScroll - 8 ? 0 : next,
        behavior: "smooth",
      });
    }, 2600);

    return () => clearInterval(timer);
  }, [hotAutoPaused, hotGames.length]);

  const recentActivity = useMemo(() => {
    const walletRows = walletActivity.map((row) => ({
      id: `wallet-${row.id}`,
      kind: "wallet",
      created_at: row.created_at,
      row,
    }));
    const spinRows = history.map((row) => ({
      id: `spin-${row.id}`,
      kind: "spin",
      created_at: row.created_at,
      row,
    }));
    return [...walletRows, ...spinRows]
      .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
      .slice(0, 6);
  }, [history, walletActivity]);

  if (!user) return (
    <div className={isDark ? "min-h-screen bg-[#05070D] text-white" : "min-h-screen bg-white text-slate-950"}>
      <Navbar theme="light" />
      <div className="max-w-md mx-auto px-6 py-32 text-center">
        <h1 className="font-display text-3xl font-bold mb-4">Sign in to view your dashboard</h1>
        <Link to="/login" className="px-8 py-3 bg-[#2F6FA5] text-white font-bold rounded-full inline-block">Sign in</Link>
      </div>
    </div>
  );

  const scroll = (dir) => carouselRef.current?.scrollBy({ left: dir * 360, behavior: "smooth" });
  const needsEmailVerification = false;
  const needsPhoneVerification = !user.phone_verified;
  const identityApproved = Boolean(user.identity_verified) || identityStatus === "approved";
  const needsIdentityVerification = identityVerificationRequired && !identityApproved;
  const freeplayProfileMissing = [
    !String(user.full_name || "").trim() ? "full legal name" : "",
    !String(user.date_of_birth || "").trim() ? "date of birth" : "",
    !String(user.state || "").trim() ? "state" : "",
  ].filter(Boolean);
  const lastFreeplayProfileMissing = freeplayProfileMissing[freeplayProfileMissing.length - 1];
  const freeplayProfileMessage = freeplayProfileMissing.length
    ? `Add your ${freeplayProfileMissing.length === 1 ? freeplayProfileMissing[0] : `${freeplayProfileMissing.slice(0, -1).join(", ")}${freeplayProfileMissing.length > 2 ? "," : ""} and ${lastFreeplayProfileMissing}`} before claiming Freeplay.`
    : "";
  const canClaimPhoneBonus = user.phone_verified && !user.bonus_claimed && freeplayProfileMissing.length === 0;
  const phoneBonusRemaining = Number(user.phone_bonus_free_play_remaining || 0);
  const phoneBonusBlockedReason = String(user.bonus_blocked_reason || "");
  const phoneBonusStatusMessage = user.phone_verified && !canClaimPhoneBonus
    ? (!user.bonus_claimed && freeplayProfileMessage)
      || (phoneBonusBlockedReason === "device_bonus_already_claimed"
      ? "$5 Freeplay was already claimed on this device, so this account cannot claim it again."
      : user.bonus_claimed
        ? phoneBonusRemaining > 0
          ? "$5 Freeplay is already added to this account. Use it from the game Load action."
          : "$5 Freeplay was already claimed for this account."
        : "")
    : "";
  const showPhoneBonusBanner = needsEmailVerification || needsPhoneVerification || canClaimPhoneBonus || Boolean(phoneBonusStatusMessage);
  const claimLeaderboardReward = async () => {
    if (rewardClaiming) return;
    setRewardClaiming(true);
    try {
      const { data } = await api.post("/me/leaderboard-reward/claim");
      setRewardClaim(data);
      await refreshUser?.();
      await api.get("/leaderboard").then(({ data: rows }) => setLeaderboard(rows)).catch(() => {});
      toast.success(`${moneyFromDollars(data?.reward?.reward_amount || data?.transaction?.amount / 1000 || 0)} Freeplay claimed`);
    } catch (err) {
      toast.error(formatApiError(err));
      await loadLeaderboardRewardClaim();
    } finally {
      setRewardClaiming(false);
    }
  };
  const claimAchievementReward = async (achievement) => {
    if (!achievement?.id || achievementClaiming) return;
    setAchievementClaiming(achievement.id);
    try {
      const { data } = await api.post(`/me/achievements/${achievement.id}/claim`);
      setAchievements(data);
      await refreshUser?.();
      await loadWalletActivity();
      toast.success(`${moneyFromDollars(data?.transaction?.amount / 1000 || achievement.reward_credits || 0)} Freeplay claimed`);
    } catch (err) {
      toast.error(formatApiError(err));
      await loadAchievements();
    } finally {
      setAchievementClaiming("");
    }
  };
  const claimPhoneBonus = async () => {
    if (bonusClaiming) return;
    setBonusClaiming(true);
    try {
      const { data } = await api.post("/auth/phone/bonus/claim");
      await refreshUser?.();
      toast.success(data?.free_play_credited ? "$5 Freeplay added" : "Phone bonus already claimed");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBonusClaiming(false);
    }
  };
  const claimBirthdayBonus = async () => {
    if (birthdayClaiming) return;
    setBirthdayClaiming(true);
    try {
      const { data } = await api.post("/me/birthday-bonus/claim");
      setBirthdayBonus(data);
      await refreshUser?.();
      await loadWalletActivity();
      toast.success(data?.transaction?.amount ? "$5 Birthday Freeplay added" : "Birthday Freeplay already claimed");
    } catch (err) {
      toast.error(formatApiError(err));
      await loadBirthdayBonus();
    } finally {
      setBirthdayClaiming(false);
    }
  };
  const claimDailySpin = async () => {
    if (dailySpinClaiming) return;
    if (dailySpinRevealTimer.current) {
      window.clearTimeout(dailySpinRevealTimer.current);
    }
    setDailySpinClaiming(true);
    setDailySpinResult(null);
    setDailySpinResultVisible(false);
    try {
      const { data } = await api.post("/me/daily-spin/claim");
      setDailySpin(data);
      const reward = data?.reward || data?.transaction;
      setDailySpinResult(reward || null);
      dailySpinRevealTimer.current = window.setTimeout(async () => {
        setDailySpinResultVisible(true);
        setDailySpinClaiming(false);
        await refreshUser?.();
        await loadWalletActivity();
        const amount = Number(data?.transaction?.amount || reward?.amount || 0);
        toast.success(amount > 0 ? `${moneyFromCoins(amount)} Freeplay won` : (reward?.label || "Daily spin completed"));
      }, DAILY_SPIN_REVEAL_MS);
      return reward || null;
    } catch (err) {
      toast.error(formatApiError(err));
      await loadDailySpin();
      setDailySpinClaiming(false);
      return null;
    }
  };
  const claimPlayStreak = async (days) => {
    if (!days || playStreakClaiming) return;
    setPlayStreakClaiming(String(days));
    try {
      const { data } = await api.post(`/me/play-streak/${days}/claim`);
      setPlayStreak(data);
      await refreshUser?.();
      await loadWalletActivity();
      toast.success(`${moneyFromCoins(data?.transaction?.amount || 0)} streak Freeplay claimed`);
    } catch (err) {
      toast.error(formatApiError(err));
      await loadPlayStreak();
    } finally {
      setPlayStreakClaiming("");
    }
  };
  const submitIdentityVerification = async (event) => {
    event.preventDefault();
    const fullName = identityForm.fullName.trim().replace(/\s+/g, " ");
    if (fullName.length < 2) {
      toast.error("Enter your full legal name before ID verification.");
      return;
    }
    if (!identityForm.dateOfBirth) {
      toast.error("Enter your date of birth before ID verification.");
      return;
    }
    if (!identityForm.state) {
      toast.error("Choose your state before ID verification.");
      return;
    }
    setIdentitySubmitting(true);
    try {
      const { data } = await api.post("/me/identity-verification/didit-session", {
        full_name: fullName,
        date_of_birth: identityForm.dateOfBirth,
        state: identityForm.state,
      });
      setIdentityForm({ fullName, dateOfBirth: identityForm.dateOfBirth, state: identityForm.state });
      await refreshUser?.();
      toast.success("Opening secure ID verification");
      window.location.assign(data.url);
    } catch (err) {
      toast.error(formatApiError(err));
      setIdentitySubmitting(false);
    }
  };
  const resendEmailVerification = async () => {
    if (emailSending || !user?.email) return;
    setEmailSending(true);
    try {
      const { data } = await api.post("/auth/email/resend", { email: user.email });
      if (data?.already_verified) {
        await refreshUser?.();
        setEmailCodeOpen(false);
      } else {
        setEmailCodeOpen(true);
      }
      toast.success(data?.message || "Verification code sent. Check your Gmail.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setEmailSending(false);
    }
  };
  const confirmEmailVerification = async (event) => {
    event.preventDefault();
    const cleanCode = emailCode.replace(/\D/g, "");
    if (cleanCode.length !== 6) {
      toast.error("Enter the 6-digit Gmail code.");
      return;
    }
    setEmailVerifying(true);
    try {
      const { data } = await api.post("/auth/email/verify", {
        email: user.email,
        code: cleanCode,
      });
      login?.(data.token, data.user);
      setEmailCode("");
      setEmailCodeOpen(false);
      await refreshUser?.();
      toast.success("Email verified");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setEmailVerifying(false);
    }
  };

  return (
    <div className={isDark ? "min-h-screen overflow-x-clip bg-[#05070D] text-white" : "min-h-screen overflow-x-clip bg-white text-slate-950"}>
      <Navbar theme="light" />

      <section className="max-w-7xl mx-auto w-full px-3 sm:px-6 md:px-10 pt-5 sm:pt-8 md:pt-10 pb-14 sm:pb-16">
        <div className={`rounded-full border px-2 py-1.5 flex items-center gap-3 text-sm overflow-hidden mb-8 shadow-sm ${
          isDark ? "border-white/10 bg-[#081421]" : "border-slate-200 bg-white"
        }`} data-testid="live-wins-ticker">
          <span className="px-3 py-0.5 rounded-full bg-[#2F6FA5] text-white font-bold text-xs uppercase tracking-wider whitespace-nowrap shrink-0">Live wins</span>
          <span className={`truncate flex-1 fade-up ${isDark ? "text-slate-200" : "text-slate-700"}`} key={tickerIdx}>{FAKE_WINS[tickerIdx]}</span>
        </div>

        <PasskeySettings dark={isDark} />

        {(communications.notice_message || Number(communications.bad_beat_jackpot || 0) > 0 || communications.notifications?.length > 0) && (
          <div className={`mb-6 rounded-2xl border border-[#2F6FA5]/20 p-4 shadow-sm sm:p-5 ${
            isDark ? "bg-[#081421] text-white" : "bg-white text-slate-950"
          }`}>
            <div className="grid gap-4 md:grid-cols-[1fr_auto] md:items-center">
              <div className="min-w-0">
                <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-[#2F6FA5]">
                  <Megaphone className="h-4 w-4" />
                  Club notice
                </div>
                {communications.notice_message ? (
                  <p className={`whitespace-pre-line text-sm ${isDark ? "text-slate-200" : "text-slate-700"}`}>{communications.notice_message}</p>
                ) : (
                  <p className="text-sm text-slate-500">Watch this space for {siteConfig.name} updates.</p>
                )}
                {communications.notifications?.[0] && (
                  <div className={`mt-3 rounded-xl border p-3 ${isDark ? "border-white/10 bg-[#050B14]" : "border-slate-200 bg-slate-50"}`}>
                    <div className="text-sm font-semibold">{communications.notifications[0].title}</div>
                    <div className={`mt-1 text-sm ${isDark ? "text-slate-300" : "text-slate-600"}`}>{communications.notifications[0].message}</div>
                  </div>
                )}
              </div>
              {Number(communications.bad_beat_jackpot || 0) > 0 && (
                <div className="rounded-xl border border-[#2F6FA5]/20 bg-[#EAF4FC] px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-2 text-xs font-bold uppercase tracking-[0.16em] text-slate-500">
                    <BadgeDollarSign className="h-4 w-4 text-[#2F6FA5]" />
                    Bad-beat jackpot
                  </div>
                  <div className="mt-1 text-2xl font-black text-[#2F6FA5]">${Number(communications.bad_beat_jackpot || 0).toFixed(2)}</div>
                </div>
              )}
            </div>
          </div>
        )}

        <WalletHeroCard user={user} className="mb-6" onBalanceChange={() => refreshUser?.()} />

        <AppDownloadCard dark={isDark} className="mb-6" />

        {birthdayBonus?.visible && (
          <div className={`mb-6 rounded-2xl border-2 border-[#2F6FA5]/25 p-5 shadow-sm ${
            isDark ? "bg-[#081421] text-white" : "bg-white text-slate-950"
          }`}>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 items-center gap-4">
                <div className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-gradient-to-br from-[#2F6FA5] to-emerald-400">
                  <Gift className="h-5 w-5 text-white" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-bold text-lg leading-tight">Birthday Freeplay</h3>
                  <p className={`mt-0.5 text-sm ${isDark ? "text-slate-300" : "text-slate-600"}`}>
                    {birthdayBonus.claimed ? "Birthday Freeplay claimed for this year." : "Claim $5 Freeplay today."}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={claimBirthdayBonus}
                disabled={!birthdayBonus.claimable || birthdayClaiming}
                className="w-full rounded-full bg-[#2F6FA5] px-5 py-2.5 font-bold text-white hover:bg-[#4E91C8] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {birthdayClaiming ? "Claiming..." : birthdayBonus.claimed ? "Claimed" : "Claim $5 FP"}
              </button>
            </div>
          </div>
        )}

        {showPhoneBonusBanner && (
          <div data-testid="verify-phone-banner"
            className={`mb-10 rounded-2xl border-2 border-[#2F6FA5]/25 p-5 shadow-sm ${
              isDark ? "bg-[#081421] text-white" : "bg-white text-slate-950"
            }`}>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-4 min-w-0">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-sky-400 to-purple-500 grid place-items-center shrink-0">
                <Phone className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0">
                <h3 className="font-bold text-lg leading-tight">
                  {needsPhoneVerification ? "Finish phone verification" : canClaimPhoneBonus ? "Claim your $5 Freeplay" : "$5 Freeplay status"}
                </h3>
                <p className={`text-sm mt-0.5 ${isDark ? "text-slate-300" : "text-slate-600"}`}>
                  {needsPhoneVerification
                    ? "Verify phone to claim your $5 Freeplay bonus."
                    : canClaimPhoneBonus
                      ? "Your phone is verified. Tap claim to add it to your Freeplay balance."
                      : phoneBonusStatusMessage}
                </p>
              </div>
            </div>
              <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
                {needsEmailVerification && (
                  <button
                    type="button"
                    onClick={resendEmailVerification}
                    disabled={emailSending}
                    data-testid="send-email-verification"
                    className="w-full sm:w-auto px-5 py-2.5 border border-slate-200 bg-white text-slate-800 font-bold rounded-full hover:bg-slate-50 whitespace-nowrap shrink-0 disabled:opacity-60"
                  >
                    {emailSending ? "Sending..." : "Send Gmail code"}
                  </button>
                )}
                {needsPhoneVerification && (
                  <button
                    type="button"
                    onClick={() => setVerifyOpen(true)}
                    data-testid="open-verify-phone"
                    className="w-full sm:w-auto px-5 py-2.5 bg-[#2F6FA5] text-white font-bold rounded-full hover:bg-[#4E91C8] whitespace-nowrap shrink-0"
                  >
                    Verify phone
                  </button>
                )}
                {canClaimPhoneBonus && (
                  <button
                    type="button"
                    onClick={claimPhoneBonus}
                    disabled={bonusClaiming}
                    data-testid="claim-phone-bonus"
                    className="w-full sm:w-auto px-5 py-2.5 bg-[#2F6FA5] text-white font-bold rounded-full hover:bg-[#4E91C8] whitespace-nowrap shrink-0 disabled:opacity-60"
                  >
                    {bonusClaiming ? "Claiming..." : "Claim $5 FP"}
                  </button>
                )}
              </div>
            </div>
            {needsEmailVerification && emailCodeOpen && (
              <form onSubmit={confirmEmailVerification} className="mt-4 flex flex-col gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-3 sm:flex-row">
                <input
                  value={emailCode}
                  onChange={(event) => setEmailCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="Gmail code"
                  data-testid="dashboard-email-code"
                  className="min-w-0 flex-1 rounded-xl border border-slate-200 bg-white px-4 py-3 text-center font-mono text-lg tracking-[0.35em] text-slate-950 outline-none focus:border-[#2F6FA5]"
                />
                <button
                  type="submit"
                  disabled={emailVerifying}
                  data-testid="dashboard-verify-email"
                  className="rounded-full bg-[#2F6FA5] px-5 py-3 font-bold text-white hover:bg-[#4E91C8] disabled:opacity-60"
                >
                  {emailVerifying ? "Verifying..." : "Verify Gmail"}
                </button>
              </form>
            )}
          </div>
        )}

        {needsIdentityVerification && (
          <IdentityVerificationCard
            dark={isDark}
            form={identityForm}
            onFullNameChange={(fullName) => setIdentityForm((current) => ({ ...current, fullName }))}
            onDateOfBirthChange={(dateOfBirth) => setIdentityForm((current) => ({ ...current, dateOfBirth }))}
            onStateChange={(state) => setIdentityForm((current) => ({ ...current, state }))}
            onSubmit={submitIdentityVerification}
            phoneVerified={Boolean(user.phone_verified)}
            saving={identitySubmitting}
            status={identityStatus}
            rejectionReason={user.identity_rejection_reason || ""}
            pendingReason={user.identity_pending_reason || ""}
            continueUrl={user.didit_session_url || ""}
          />
        )}

        <DailyRewardsCard
          dark={isDark}
          dailySpin={dailySpin}
          dailySpinClaiming={dailySpinClaiming}
          dailySpinResult={dailySpinResultVisible ? dailySpinResult : null}
          onDailySpin={claimDailySpin}
          playStreak={playStreak}
          playStreakClaiming={playStreakClaiming}
          onPlayStreakClaim={claimPlayStreak}
        />

        <div className="mb-10">
          <PlinkoHomeGame dark={isDark} />
        </div>

        <div className="mb-10">
          <CrashHomeGame dark={isDark} />
        </div>

        <AchievementRewardsCard
          payload={achievements}
          dark={isDark}
          claimingId={achievementClaiming}
          onClaim={claimAchievementReward}
        />

        <div className="mb-12">
          <div className="flex items-center justify-between mb-5">
            <div className="flex min-w-0 items-center gap-2.5">
              <Flame className="w-5 h-5 text-orange-400" />
              <h2 className="font-display text-xl font-bold sm:text-2xl md:text-3xl">Hot Games</h2>
            </div>
            <div className="flex items-center gap-2">
              <Link to="/games" className="text-sm text-[#2F6FA5] hover:underline">View all</Link>
              <button onClick={() => scroll(-1)} aria-label="Scroll left"
                className="p-2 rounded-full border border-slate-200 text-slate-700 hover:bg-slate-100 hidden md:inline-flex">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button onClick={() => scroll(1)} aria-label="Scroll right"
                className="p-2 rounded-full border border-slate-200 text-slate-700 hover:bg-slate-100 hidden md:inline-flex">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div ref={carouselRef}
            onPointerEnter={() => setHotAutoPaused(true)}
            onPointerLeave={() => setHotAutoPaused(false)}
            onTouchStart={() => setHotAutoPaused(true)}
            onTouchEnd={() => setHotAutoPaused(false)}
            className="hot-games-carousel -mx-4 sm:mx-0 flex max-w-[100vw] gap-4 overflow-x-auto overscroll-x-contain px-4 sm:px-0 pb-3 snap-x snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            data-testid="hot-games-carousel">
            {hotGames.map((g, index) => (
              <Link key={g.id} to={`/games/${g.id}`} data-testid={`hot-game-${g.id}`}
                data-hot-game-card
                style={{ "--hot-index": index }}
                className="hot-game-card snap-start w-[38vw] max-w-36 shrink-0 sm:w-44 sm:max-w-none md:w-52 group">
                <div className="hot-game-art relative aspect-[5/4] rounded-2xl overflow-hidden border-2 border-transparent group-hover:border-[#2F6FA5]/60 transition-all">
                  {g.image_url ? (
                    <img src={g.image_url} alt={g.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-[#0A2238] to-[#05070D]" />
                  )}
                </div>
                <div className="mt-3 text-center font-semibold">{g.name}</div>
              </Link>
            ))}
          </div>
        </div>

        <div className="grid min-w-0 gap-6 md:grid-cols-3">
          <div className={`min-w-0 rounded-2xl border p-5 shadow-sm sm:p-6 ${
            isDark ? "border-white/10 bg-[#081421] text-white" : "border-slate-200 bg-white text-slate-950"
          }`}>
            <div className="flex items-center gap-2 text-slate-500 text-sm mb-4"><History className="w-4 h-4" /> Recent activity</div>
            <div className="space-y-1.5 text-sm">
              {recentActivity.length === 0 && <div className="text-slate-400 py-6 text-center">No activity yet.</div>}
              {recentActivity.map((item) => (
                <DashboardActivityRow key={item.id} item={item} dark={isDark} />
              ))}
            </div>
          </div>

          <div className={`min-w-0 rounded-2xl border p-5 shadow-sm sm:p-6 ${
            isDark ? "border-white/10 bg-[#081421] text-white" : "border-slate-200 bg-white text-slate-950"
          }`}>
            <div className="flex items-center gap-2 text-slate-500 text-sm mb-4"><Trophy className="w-4 h-4" /> Top players</div>
            <div className="space-y-1.5 text-sm">
              {leaderboard.slice(0, 5).map((u, i) => (
                <div key={u.username + i} className="flex min-w-0 items-center justify-between gap-3 py-1.5 border-b border-slate-100">
                  <span className="flex min-w-0 items-center gap-3">
                    <span className="font-display font-bold gold-text w-6 shrink-0">#{i + 1}</span>
                    <VerifiedName name={u.username} user={u} />
                  </span>
                  <span className="shrink-0 text-right">
                    <span className="block tabular-nums text-slate-600">{leaderMoney(u)}</span>
                    {u.reward && (
                      <span className="mt-0.5 inline-flex rounded-full border border-[#2F6FA5]/40 bg-[#2F6FA5]/10 px-2 py-0.5 text-[10px] font-black text-[#B7D7F2]">
                        {u.reward.label}
                      </span>
                    )}
                    {rewardClaim?.eligible && Number(rewardClaim.rank) === i + 1 && (
                      <button
                        type="button"
                        onClick={claimLeaderboardReward}
                        disabled={rewardClaiming || rewardClaim.claimed || !rewardClaim.claim_open}
                        className="mt-1 block w-full rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-black text-emerald-700 disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400"
                      >
                        {leaderboardClaimLabel(rewardClaim, rewardClaiming)}
                      </button>
                    )}
                  </span>
                </div>
              ))}
            </div>
            {rewardClaim?.eligible && (
              <p className="mt-3 text-xs leading-relaxed text-slate-500">
                Top-player freeplay claims open Saturday. All eligible freeplay redemptions deduct 60%; 40% is credited to your wallet.
              </p>
            )}
          </div>

          <ReferralCard />
        </div>
      </section>

      <VerifyPhoneDialog open={verifyOpen} onClose={() => setVerifyOpen(false)} onVerified={loadWalletActivity} />
      <Footer theme="light" />
    </div>
  );
}

function AchievementRewardsCard({ payload, dark, claimingId, onClaim }) {
  if (!payload) return null;
  const settings = payload.settings || {};
  const achievements = payload.achievements || [];
  if (!achievements.length && settings.achievements_enabled !== false) return null;

  return (
    <section className={`mb-10 rounded-2xl border p-5 shadow-sm sm:p-6 ${
      dark ? "border-white/10 bg-[#081421] text-white" : "border-slate-200 bg-white text-slate-950"
    }`}>
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-[#2F6FA5]">
            <Gift className="h-4 w-4" />
            Rewards
          </div>
          <h2 className="mt-1 font-display text-2xl font-bold">Achievements</h2>
          <p className={`mt-1 text-sm ${dark ? "text-slate-300" : "text-slate-600"}`}>
            Complete goals and claim Freeplay rewards.
          </p>
        </div>
      </div>

      {settings.achievements_enabled === false ? (
        <div className={`rounded-xl border p-4 text-sm ${dark ? "border-amber-400/30 bg-amber-400/10 text-amber-100" : "border-amber-200 bg-amber-50 text-amber-800"}`}>
          {settings.achievements_locked_message || "Achievements are currently paused."}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {achievements.map((item) => {
            const Icon = achievementIcon(item.icon);
            const busy = claimingId === item.id;
            return (
              <div key={item.id} className={`rounded-2xl border p-4 ${dark ? "border-white/10 bg-[#050B14]" : "border-slate-200 bg-slate-50"}`}>
                <div className="flex items-start gap-3">
                  <div className={`grid h-11 w-11 shrink-0 place-items-center rounded-xl ${item.claimed ? "bg-emerald-500/15 text-emerald-500" : "bg-[#2F6FA5]/15 text-[#2F6FA5]"}`}>
                    {item.claimed ? <CheckCircle2 className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0">
                        <h3 className="truncate font-bold">{item.title}</h3>
                        {item.description && (
                          <p className={`mt-1 text-sm leading-relaxed ${dark ? "text-slate-300" : "text-slate-600"}`}>{item.description}</p>
                        )}
                      </div>
                      <div className="shrink-0 rounded-full border border-emerald-300/40 bg-emerald-500/10 px-3 py-1 text-xs font-black text-emerald-500">
                        {item.reward_label || `${moneyFromDollars(item.reward_credits || 0)} Freeplay`}
                      </div>
                    </div>

                    <div className="mt-4">
                      <div className="mb-2 flex items-center justify-between gap-3 text-xs">
                        <span className={`font-bold uppercase tracking-[0.14em] ${dark ? "text-slate-400" : "text-slate-500"}`}>
                          {achievementGoalLabel(item.goal)}
                        </span>
                        <span className="font-semibold tabular-nums">
                          {formatAchievementProgress(item.progress, item.goal)} / {formatAchievementProgress(item.target_next, item.goal)}
                        </span>
                      </div>
                      <div className={`h-2 overflow-hidden rounded-full ${dark ? "bg-white/10" : "bg-slate-200"}`}>
                        <div
                          className="h-full rounded-full bg-[#2F6FA5] transition-all"
                          style={{ width: `${Math.max(0, Math.min(100, Number(item.progress_percent || 0)))}%` }}
                        />
                      </div>
                    </div>

                    {item.locked_reason && !item.claimable && (
                      <div className={`mt-3 flex items-start gap-2 rounded-xl border px-3 py-2 text-xs ${dark ? "border-white/10 bg-black/20 text-slate-300" : "border-slate-200 bg-white text-slate-600"}`}>
                        <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                        <span>{item.locked_reason}</span>
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={() => onClaim?.(item)}
                      disabled={!item.claimable || busy}
                      className={`mt-4 w-full rounded-full px-4 py-2.5 text-sm font-bold transition ${
                        item.claimable
                          ? "bg-[#2F6FA5] text-white hover:bg-[#4E91C8]"
                          : dark
                            ? "bg-white/10 text-slate-500"
                            : "bg-slate-200 text-slate-500"
                      } disabled:cursor-not-allowed disabled:opacity-70`}
                    >
                      {achievementClaimLabel(item, busy)}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function IdentityVerificationCard({
  dark,
  continueUrl,
  form,
  onDateOfBirthChange,
  onFullNameChange,
  onStateChange,
  onSubmit,
  phoneVerified,
  pendingReason,
  rejectionReason,
  saving,
  status,
}) {
  const pending = status === "pending";
  const rejected = status === "rejected";
  const expired = rejected && isKycExpiredReason(rejectionReason);
  return (
    <form onSubmit={onSubmit} className={`mb-10 rounded-2xl border-2 p-5 shadow-sm ${
      rejected
        ? dark ? "border-rose-400/30 bg-rose-950/20 text-white" : "border-rose-200 bg-rose-50 text-slate-950"
        : dark ? "border-[#2F6FA5]/30 bg-[#081421] text-white" : "border-[#2F6FA5]/25 bg-white text-slate-950"
    }`}>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-4">
          <div className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-[#2F6FA5] text-white">
            <IdCard className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h3 className="font-bold text-lg leading-tight">Secure ID verification</h3>
            <p className={`mt-1 text-sm ${dark ? "text-slate-300" : "text-slate-600"}`}>
              {pending
                ? "Your Didit verification is in progress. Continue if you have not finished it yet."
                : rejected
                  ? expired
                    ? "Your last Didit session expired before it was finished. Start again when you are ready."
                    : "Your verification was not approved. Review the reason below and retry."
                  : "Secure ID verification is required only before wallet withdrawal. It usually takes 1-2 minutes."}
            </p>
            {!phoneVerified && (
              <p className={`mt-2 text-xs font-semibold ${dark ? "text-amber-200" : "text-amber-700"}`}>
                Phone verification is also required.
              </p>
            )}
          </div>
        </div>
        <span className={`rounded-full px-3 py-1 text-xs font-black uppercase tracking-[0.12em] ${
          pending
            ? "bg-amber-100 text-amber-700"
            : rejected
              ? "bg-rose-100 text-rose-700"
              : "bg-sky-100 text-sky-700"
        }`}>
          {pending ? "pending" : rejected ? "rejected" : "required"}
        </span>
      </div>

      {rejected && (
        <div className={`mt-4 rounded-xl border p-4 ${
          dark ? "border-rose-400/30 bg-rose-950/30" : "border-rose-200 bg-white"
        }`}>
          <p className={`text-xs font-black uppercase tracking-[0.14em] ${dark ? "text-rose-300" : "text-rose-700"}`}>
            {expired ? "Session expired" : "Why verification was rejected"}
          </p>
          <p className={`mt-2 text-sm font-semibold leading-6 ${dark ? "text-rose-100" : "text-rose-900"}`}>
            {formatKycRejectionReason(rejectionReason)}
          </p>
          <p className={`mt-2 text-xs ${dark ? "text-slate-300" : "text-slate-600"}`}>
            {expired
              ? "This is not a permanent rejection. Start KYC again and finish every Didit step before the timer ends."
              : "Fix the issue above, then submit verification again. You can resubmit from this same form."}
          </p>
        </div>
      )}

      {pending && (
        <div className={`mt-4 rounded-xl border p-4 ${
          dark ? "border-amber-400/30 bg-amber-950/30" : "border-amber-200 bg-amber-50"
        }`}>
          <p className={`text-xs font-black uppercase tracking-[0.14em] ${dark ? "text-amber-300" : "text-amber-700"}`}>
            Why verification is pending
          </p>
          <p className={`mt-2 text-sm font-semibold leading-6 ${dark ? "text-amber-100" : "text-amber-900"}`}>
            {formatKycPendingReason(pendingReason)}
          </p>
          <p className={`mt-2 text-xs ${dark ? "text-slate-300" : "text-slate-600"}`}>
            Complete the remaining Didit step, then tap continue or refresh the page.
          </p>
        </div>
      )}

      {!pending && (
        <>
          <div className={`mt-4 rounded-xl border p-4 ${
            dark ? "border-sky-400/20 bg-sky-950/20" : "border-sky-100 bg-sky-50"
          }`}>
            <p className={`text-xs font-black uppercase tracking-[0.14em] ${dark ? "text-sky-200" : "text-[#2F6FA5]"}`}>
              Before you start
            </p>
            <ul className={`mt-3 space-y-2 text-sm font-semibold leading-5 ${dark ? "text-slate-200" : "text-slate-700"}`}>
              {kycPrepItems().map((item) => (
                <li key={item} className="flex gap-2">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#2F6FA5]" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <label className="mt-5 block">
            <span className={`mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] ${dark ? "text-slate-400" : "text-slate-500"}`}>
              <User className="h-3.5 w-3.5" /> Full legal name
            </span>
            <input
              value={form.fullName}
              onChange={(event) => onFullNameChange(event.target.value)}
              minLength={2}
              maxLength={100}
              required
              placeholder="Name exactly as shown on ID"
              className={`w-full rounded-xl border px-4 py-3 outline-none transition focus:border-[#2F6FA5] focus:ring-2 focus:ring-[#2F6FA5]/20 ${
                dark ? "border-white/10 bg-[#050B14] text-white placeholder:text-slate-500" : "border-slate-200 bg-white text-slate-950"
              }`}
            />
          </label>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className={`mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] ${dark ? "text-slate-400" : "text-slate-500"}`}>
                <CalendarDays className="h-3.5 w-3.5" /> Date of birth
              </span>
              <input
                type="date"
                value={form.dateOfBirth}
                onChange={(event) => onDateOfBirthChange(event.target.value)}
                max={todayInputValue()}
                required
                className={`w-full rounded-xl border px-4 py-3 outline-none transition focus:border-[#2F6FA5] focus:ring-2 focus:ring-[#2F6FA5]/20 ${
                  dark ? "border-white/10 bg-[#050B14] text-white" : "border-slate-200 bg-white text-slate-950"
                }`}
              />
            </label>
            <label className="block">
              <span className={`mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] ${dark ? "text-slate-400" : "text-slate-500"}`}>
                <MapPin className="h-3.5 w-3.5" /> State
              </span>
              <select
                value={form.state}
                onChange={(event) => onStateChange(event.target.value)}
                required
                className={`w-full rounded-xl border px-4 py-3 outline-none transition focus:border-[#2F6FA5] focus:ring-2 focus:ring-[#2F6FA5]/20 ${
                  dark ? "border-white/10 bg-[#050B14] text-white" : "border-slate-200 bg-white text-slate-950"
                }`}
              >
                <option value="">Select state</option>
                {US_STATES.map((item) => (
                  <option key={item.code} value={item.code}>{item.name}</option>
                ))}
              </select>
            </label>
          </div>
          <button
            type="submit"
            disabled={saving || form.fullName.trim().length < 2 || !form.dateOfBirth || !form.state}
            className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#2F6FA5] px-5 py-3 font-bold text-white hover:bg-[#4E91C8] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
          >
            <ShieldCheck className="h-4 w-4" />
            {saving ? "Opening..." : rejected ? kycRetryLabel(rejectionReason) : "Start secure KYC"}
          </button>
        </>
      )}
      {pending && continueUrl && (
        <button
          type="button"
          onClick={() => window.location.assign(continueUrl)}
          className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#2F6FA5] px-5 py-3 font-bold text-white hover:bg-[#4E91C8] sm:w-auto"
        >
          <ExternalLink className="h-4 w-4" />
          Continue verification
        </button>
      )}
    </form>
  );
}

function todayInputValue() {
  return new Date().toISOString().slice(0, 10);
}

function DailyRewardsCard({
  dark,
  dailySpin,
  dailySpinClaiming,
  dailySpinResult,
  onDailySpin,
  playStreak,
  playStreakClaiming,
  onPlayStreakClaim,
}) {
  const [spinRotation, setSpinRotation] = useState(0);
  if (!dailySpin && !playStreak) return null;
  const spinClaimable = Boolean(dailySpin?.claimable);
  const spinReward = dailySpinResult || (!dailySpinClaiming ? dailySpin?.reward : null);
  const segments = dailySpin?.segments?.length ? dailySpin.segments : [
    { label: "$0.10" },
    { label: "$0.20" },
    { label: "$0.30" },
    { label: "20% bonus" },
    { label: "Better luck" },
    { label: "30% bonus" },
    { label: "$1.00" },
    { label: "50% bonus" },
  ];
  const wheelSegments = segments.some((item) => String(item?.label || "").toLowerCase() === "30% bonus")
    ? segments.slice(0, 8)
    : [...segments.slice(0, -1), { id: "bonus-30", label: "30% bonus" }, ...segments.slice(-1)].slice(0, 8);
  const streakDays = Number(playStreak?.streak_days || 0);
  const nextReward = playStreak?.next_reward;
  const nextRewardText = nextReward
    ? `Next reward: ${moneyFromDollars(nextReward.amount)} at ${nextReward.days} days · ${Math.max(0, Number(playStreak.days_to_go || 0))} days to go`
    : (playStreak?.next_reward_label || "All streak rewards claimed");
  const wheelBackground = "radial-gradient(circle at 50% 50%, transparent 0 35%, rgba(90,74,48,0.14) 35.5% 36.5%, transparent 37%), conic-gradient(from -22.5deg,#FBF8F0 0 12.5%,#ECE6DA 12.5% 25%,#FFFDF8 25% 37.5%,#E5DED1 37.5% 50%,#FBF8F0 50% 62.5%,#EDE7DC 62.5% 75%,#FFFDF8 75% 87.5%,#E7E0D4 87.5% 100%)";
  const handleDailySpinClick = async () => {
    if (!spinClaimable || dailySpinClaiming) return;
    const reward = await onDailySpin?.();
    if (!reward) return;
    const rewardIndex = dailySpinRewardIndex(wheelSegments, reward);
    setSpinRotation((current) => nextDailySpinRotation(current, rewardIndex));
  };
  return (
    <section className={`mb-10 rounded-2xl border p-5 shadow-sm sm:p-6 ${
      dark ? "border-white/10 bg-[#081421] text-white" : "border-slate-200 bg-white text-slate-950"
    }`}>
      <div className="mb-5 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-[#2F6FA5]">
        <Gift className="h-4 w-4" />
        Daily Rewards
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <div className={`rounded-2xl border p-4 ${dark ? "border-white/10 bg-[#050B14]" : "border-slate-200 bg-slate-50"}`}>
          <div className="mb-3 text-center">
            <div className={`text-xs font-bold uppercase tracking-[0.16em] ${dark ? "text-slate-400" : "text-slate-500"}`}>Daily Spin</div>
            <h3 className="mt-1 text-2xl font-black">Ready to spin?</h3>
          </div>
          <div className="relative mx-auto h-[360px] w-full max-w-[390px] sm:h-[390px]">
            <div className="absolute left-1/2 top-0 z-20 h-12 w-7 -translate-x-1/2 rounded-b-full bg-gradient-to-b from-[#FFE08A] to-[#F3A300] shadow-lg shadow-amber-500/30 ring-1 ring-amber-700/10" />
            <div className="absolute left-1/2 top-6 h-[310px] w-[310px] -translate-x-1/2 overflow-hidden rounded-full border-[14px] border-[#D8D0C1] shadow-2xl shadow-black/35 sm:h-[340px] sm:w-[340px]">
              <div
                className="absolute inset-0 rounded-full"
                style={{ background: wheelBackground, transform: `rotate(${spinRotation}deg)`, transition: "transform 2600ms cubic-bezier(0.12, 0.72, 0.12, 1)" }}
              >
                <div className="absolute inset-0 rounded-full shadow-[inset_0_0_34px_rgba(96,80,55,0.20)]" />
                <div className="absolute inset-[35%] rounded-full border-2 border-[#9AD9C5]/80 bg-[#FFFDF8]/95 shadow-inner shadow-slate-300/70" />
                {wheelSegments.map((item, index) => {
                  const angle = (index * 45) - 90;
                  const radius = 38;
                  const x = 50 + Math.cos((angle * Math.PI) / 180) * radius;
                  const y = 50 + Math.sin((angle * Math.PI) / 180) * radius;
                  return (
                    <span
                      key={`${item.id || item.label}-${index}`}
                      className="absolute flex max-w-[84px] -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center gap-0.5 text-center text-[12px] font-black leading-none text-[#2B2822] drop-shadow-[0_1px_1px_rgba(255,255,255,0.95)]"
                      style={{ left: `${x}%`, top: `${y}%`, transform: `translate(-50%, -50%) rotate(${angle + 90}deg)` }}
                    >
                      <span className="text-xl leading-none" aria-hidden="true">{dailySpinPrizeIcon(item.label)}</span>
                      <span>{item.label}</span>
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
          {spinReward && (
            <div className={`mb-3 rounded-xl border px-3 py-2 text-center text-sm font-bold ${
              dark ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-200" : "border-emerald-200 bg-emerald-50 text-emerald-700"
            }`}>
              Result: {dailySpinPrizeIcon(spinReward.label)} {spinReward.label || (Number(spinReward.amount || 0) > 0 ? `${moneyFromCoins(spinReward.amount)} Freeplay` : "Daily spin")}
            </div>
          )}
          {dailySpin?.locked_reason && !spinClaimable && (
            <div className={`mb-3 rounded-xl border px-3 py-2 text-sm ${dark ? "border-white/10 bg-black/20 text-slate-300" : "border-slate-200 bg-white text-slate-600"}`}>
              {dailySpin.locked_reason}
            </div>
          )}
          <button
            type="button"
            onClick={handleDailySpinClick}
            disabled={!spinClaimable || dailySpinClaiming}
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-amber-400 px-5 py-3 font-black text-slate-950 hover:bg-amber-300 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <RotateCw className={`h-4 w-4 ${dailySpinClaiming ? "animate-spin" : ""}`} />
            {dailySpinClaiming ? "Spinning..." : dailySpin?.claimed ? "Spin today" : "Spin"}
          </button>
        </div>

        <div className={`rounded-2xl border p-4 ${dark ? "border-white/10 bg-[#050B14]" : "border-slate-200 bg-slate-50"}`}>
          <div className="flex items-start gap-3">
            <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-emerald-500/15 text-emerald-400">
              <Flame className="h-6 w-6" />
            </div>
            <div className="min-w-0">
              <h3 className="text-2xl font-black">{streakDays} day streak</h3>
              <p className={`mt-1 text-sm ${dark ? "text-slate-300" : "text-slate-600"}`}>{nextRewardText}</p>
            </div>
          </div>
          <div className={`mt-4 rounded-xl border px-4 py-3 text-sm ${dark ? "border-amber-300/20 bg-amber-300/10 text-amber-100" : "border-amber-200 bg-amber-50 text-amber-800"}`}>
            Play a game today to start your streak. Earn $1 at 7 days, $2 at 14, $4 at 30, $7 at 60, and $18 at 90.
          </div>
          {playStreak?.locked_reason && (
            <div className={`mt-3 rounded-xl border px-3 py-2 text-sm ${dark ? "border-white/10 bg-black/20 text-slate-300" : "border-slate-200 bg-white text-slate-600"}`}>
              {playStreak.locked_reason}
            </div>
          )}
          <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3">
            {(playStreak?.milestones || []).map((item) => {
              const busy = playStreakClaiming === String(item.days);
              return (
                <button
                  key={item.days}
                  type="button"
                  onClick={() => onPlayStreakClaim?.(item.days)}
                  disabled={!item.claimable || busy}
                  className={`rounded-xl border px-3 py-3 text-center transition disabled:cursor-not-allowed ${
                    item.claimed
                      ? dark ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-200" : "border-emerald-200 bg-emerald-50 text-emerald-700"
                      : item.claimable
                        ? "border-[#2F6FA5] bg-[#2F6FA5] text-white hover:bg-[#4E91C8]"
                        : dark ? "border-white/10 bg-white/5 text-slate-400" : "border-slate-200 bg-white text-slate-500"
                  }`}
                >
                  <span className="block text-sm font-black">{item.days}D</span>
                  <span className="block text-lg font-black">{moneyFromDollars(item.amount)}</span>
                  <span className="mt-1 block text-[10px] font-bold uppercase tracking-[0.12em] opacity-80">
                    {busy ? "Claiming" : item.claimed ? "Claimed" : item.claimable ? "Claim" : "Locked"}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

function DashboardActivityRow({ item, dark }) {
  if (item.kind === "spin") {
    const row = item.row;
    return (
      <div className={`flex items-center justify-between gap-3 py-2 border-b ${dark ? "border-white/10" : "border-slate-100"}`}>
        <span className="min-w-0">
          <span className="block truncate font-semibold">Slot spin</span>
          <span className="block truncate font-mono text-xs text-slate-500">{row.reels?.join(" - ") || "-"}</span>
        </span>
        <span className={row.payout > 0 ? "text-[#2F6FA5] font-semibold" : "text-slate-400"}>
          {row.payout > 0 ? `+${row.payout.toLocaleString()}` : `-${row.bet.toLocaleString()}`}
        </span>
      </div>
    );
  }

  const row = item.row;
  const out = walletActivityIsOut(row);
  const amount = walletActivityAmount(row);
  return (
    <div className={`flex items-center justify-between gap-3 py-2 border-b ${dark ? "border-white/10" : "border-slate-100"}`}>
      <span className="min-w-0">
        <span className="block truncate font-semibold">{walletActivityLabel(row)}</span>
        <span className="block truncate text-xs text-slate-500">{walletActivityDetail(row)}</span>
      </span>
      <span className={`shrink-0 font-semibold tabular-nums ${out ? "text-amber-500" : "text-emerald-500"}`}>
        {out ? "-" : "+"}{moneyFromCoins(amount)}
      </span>
    </div>
  );
}

function dailySpinPrizeIcon(label) {
  const text = String(label || "").toLowerCase();
  if (text.includes("bonus")) return "🎁";
  if (text.includes("luck")) return "🍀";
  return "💰";
}

function dailySpinRewardIndex(segments, reward) {
  if (!reward || !Array.isArray(segments) || segments.length === 0) return 0;
  const rewardId = String(reward.id || reward.daily_spin_reward_id || "").trim();
  if (rewardId) {
    const idIndex = segments.findIndex((item) => String(item?.id || "").trim() === rewardId);
    if (idIndex >= 0) return idIndex;
  }
  const rewardLabel = normalizeDailySpinLabel(reward.label || reward.daily_spin_reward_label);
  if (rewardLabel) {
    const labelIndex = segments.findIndex((item) => normalizeDailySpinLabel(item?.label) === rewardLabel);
    if (labelIndex >= 0) return labelIndex;
  }
  const rewardAmount = Number(reward.amount || 0);
  if (Number.isFinite(rewardAmount) && rewardAmount > 0) {
    const amountIndex = segments.findIndex((item) => Number(item?.amount || 0) === rewardAmount);
    if (amountIndex >= 0) return amountIndex;
  }
  return 0;
}

function normalizeDailySpinLabel(value) {
  return String(value || "").trim().toLowerCase().replace(/\s+/g, " ");
}

function nextDailySpinRotation(current, rewardIndex) {
  const segmentAngle = 45;
  const target = (((-Number(rewardIndex || 0) * segmentAngle) % 360) + 360) % 360;
  const currentMod = ((Number(current || 0) % 360) + 360) % 360;
  const delta = (target - currentMod + 360) % 360;
  return Number(current || 0) + 1440 + delta;
}

function walletActivityIsOut(row) {
  return ["redeem", "admin_debit", "wallet_transfer_sent", "inactivity_wallet_expiry", "inactivity_freeplay_expiry"].includes(row?.type)
    || (row?.transfer_kind === "game_load" && row?.status !== "rejected");
}

function walletActivityAmount(row) {
  if (row?.transfer_kind === "game_load" && row?.status === "rejected") return 0;
  if (row?.transfer_kind === "game_load") return Number(row.amount || row.free_play_debit_amount || row.wallet_debit_amount || 0);
  if (row?.transfer_kind === "game_redeem") return Number(row.wallet_credit_amount || row.amount || 0);
  return Number(row?.amount || 0);
}

function walletActivityLabel(row) {
  if (row?.transfer_kind === "referral_wallet_credit") return "Referral wallet bonus";
  if (row?.transfer_kind === "referral_freeplay_wallet_deposit") return "Referral freeplay";
  if (row?.transfer_kind === "phone_verification_freeplay" || row?.type === "phone_verification_bonus") return "Phone verification freeplay";
  if (row?.transfer_kind === "referral_freeplay") return "Referral freeplay";
  if (row?.transfer_kind === "achievement_freeplay" || row?.type === "achievement_reward") return "Achievement freeplay";
  if (row?.transfer_kind === "weekly_leaderboard_freeplay") return "Leaderboard freeplay";
  if (row?.transfer_kind === "birthday_freeplay" || row?.type === "birthday_bonus") return "Birthday freeplay";
  if (row?.transfer_kind === "daily_spin_freeplay" || row?.type === "daily_spin_reward") return "Daily spin freeplay";
  if (row?.transfer_kind === "play_streak_freeplay" || row?.type === "play_streak_reward") return "Play streak freeplay";
  if (row?.transfer_kind === "game_load" && row?.status === "rejected") return `Game load failed${row.game_id ? `: ${displayGameName(row.game_id)}` : ""}`;
  if (row?.transfer_kind === "game_load") return `Game load${row.game_id ? `: ${displayGameName(row.game_id)}` : ""}`;
  if (row?.transfer_kind === "game_redeem") return `Game redeem${row.game_id ? `: ${displayGameName(row.game_id)}` : ""}`;
  const labels = {
    load: "Add money",
    redeem: "Withdraw money",
    admin_credit: "Wallet credit",
    admin_debit: "Wallet debit",
    inactivity_wallet_expiry: "Wallet expired: 15-day inactivity",
    inactivity_freeplay_expiry: "Freeplay expired: 15-day inactivity",
    manual_freeplay: "Freeplay credit",
    wallet_transfer_sent: "Sent to friend",
    wallet_transfer_received: "Received from friend",
  };
  return labels[row?.type] || String(row?.type || "Activity").replace(/_/g, " ");
}

function walletActivityDetail(row) {
  if (row?.transfer_kind === "game_load" && row?.status === "rejected") {
    return gameLoadFailureMessage(row);
  }
  if (row?.transfer_kind === "game_load" && Number(row.phone_free_play_debit_amount || 0) > 0) {
    return "Phone freeplay used for game load";
  }
  if (row?.status && row.status !== "approved") return row.status;
  return String(row?.note || row?.admin_note || "Approved").replace(/\s+/g, " ").trim();
}

function displayGameName(value) {
  return String(value || "game")
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function leaderMoney(row) {
  const coins = Number(row?.total_load_coins ?? row?.coins ?? 0);
  return `$${(coins / 1000).toFixed(2)}`;
}

function moneyFromCoins(value) {
  return `$${(Number(value || 0) / 1000).toFixed(2)}`;
}

function moneyFromDollars(value) {
  return `$${Number(value || 0).toFixed(2)}`;
}

function achievementIcon(value) {
  const key = String(value || "").toLowerCase();
  if (key.includes("user") || key.includes("refer")) return Users;
  if (key.includes("gift") || key.includes("bonus")) return Gift;
  if (key.includes("target") || key.includes("goal")) return Target;
  return Trophy;
}

function achievementGoalLabel(goal) {
  const labels = {
    signup: "Signup",
    deposit_count: "Approved deposits",
    deposit_total: "Deposit total",
    game_load_count: "Game loads",
    game_load_total: "Game load total",
    referrals_count: "Referrals",
    referral_deposit_count: "Referral deposits",
  };
  return labels[goal] || String(goal || "Progress").replace(/_/g, " ");
}

function formatAchievementProgress(value, goal) {
  const number = Number(value || 0);
  if (["deposit_total", "game_load_total"].includes(goal)) {
    return moneyFromDollars(number);
  }
  if (Number.isInteger(number)) return String(number);
  return number.toFixed(2).replace(/\.?0+$/, "");
}

function achievementClaimLabel(item, busy) {
  if (busy) return "Claiming...";
  if (item?.claimable) return "Claim Freeplay";
  if (item?.claimed) return "Claimed";
  return "Locked";
}

function leaderboardClaimLabel(claim, busy) {
  if (busy) return "Claiming...";
  if (claim?.claimed) return "Claimed";
  if (!claim?.claim_open) return "Claim Saturday";
  return "Claim";
}
